import React, { useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'
import SlideBase from './SlideBase'
import WrappedLottieAnimation from '../WrappedLottieAnimation'
import TextButton from 'LuxKit/Button/TextButton'
import SolidShareIcon from 'LuxKit/Icons/solid/SolidShareIcon'
import Group from 'components/utils/Group'
import { PERSONALITY_GRADIENTS, PERSONALITY_IMAGE_IDS, PERSONALITY_NAMES } from './TravelPersonalityStyles'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import SparklesAnimation from '../SparklesAnimation'
import { getImageUrl } from 'lib/image/imageUtils'
import Sheet from 'components/utils/Sheet'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import TextLink from 'LuxKit/TextLink'
import WrappedLoyaltyPercentileBadge from '../WrappedLoyaltyPercentileBadge'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import InteractiveZone from '../InteractiveZone'
import useWrappedShare from '../../hooks/useWrappedShare'
import clsx from 'clsx'
import { useAppSelector } from 'hooks/reduxHooks'
import useDistanceDisplay from 'hooks/useDistanceDisplay'

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
`

const TravelVibeCard = styled(Group)`
  border: 1px solid ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${props => props.theme.borderRadius.L};
`

const PersonalityImageCard = styled(Sheet)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${rem(76)};
  height: ${rem(76)};
  padding: ${rem(8)};
  border-radius: ${rem(9.6)};
  border: 0.48px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4.8px 9.6px rgba(0, 0, 0, 0.25);

  &.personality-the-sun-chaser {
    background: ${PERSONALITY_GRADIENTS['the-sun-chaser']};
  }
  &.personality-the-wellness-wanderer {
    background: ${PERSONALITY_GRADIENTS['the-wellness-wanderer']};
  }
  &.personality-the-nature-roamer {
    background: ${PERSONALITY_GRADIENTS['the-nature-roamer']};
  }
  &.personality-the-bond-builder {
    background: ${PERSONALITY_GRADIENTS['the-bond-builder']};
  }
  &.personality-the-romance-storyteller {
    background: ${PERSONALITY_GRADIENTS['the-romance-storyteller']};
  }
  &.personality-the-trend-explorer {
    background: ${PERSONALITY_GRADIENTS['the-trend-explorer']};
  }
  &.personality-the-gourmet-hunter {
    background: ${PERSONALITY_GRADIENTS['the-gourmet-hunter']};
  }
  &.personality-the-elite-traveller {
    background: ${PERSONALITY_GRADIENTS['the-elite-traveller']};
  }
  &.personality-the-curious-explorer {
    background: ${PERSONALITY_GRADIENTS['the-curious-explorer']};
  }
`

const PersonalityImageContainer = styled.div`
  position: relative;
  width: ${rem(60)};
  height: ${rem(60)};
  animation: ${float} 3s ease-in-out infinite;
  /* GPU acceleration for smooth animation */
  will-change: transform;
  transform: translateZ(0);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    will-change: auto;
  }
`

const PersonalityImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const SparklesOverlay = styled.div`
  position: absolute;
  inset: -30%;
  pointer-events: none;
  z-index: ${zIndex.min};
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(12)};
  width: 100%;
`

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  padding: ${rem(12)} ${rem(16)};
  border: 1px solid ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${props => props.theme.borderRadius.L};

  &.full-width {
    grid-column: 1 / -1;
  }
`

const LoyaltyRowSlot = styled(Group)`
  margin-top: ${rem(4)};
`

const LottieStyle = { width: '100%', height: '100%' }

const TopViewedOfferCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  padding: ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${props => props.theme.borderRadius.L};
  width: 100%;
`

const InclusionsContainer = styled(Group)`
  margin-top: ${rem(4)};
`

const MAX_VISIBLE_INCLUSIONS = 3

function SummarySlide() {
  // Use granular selector to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const { handleShare, isCapturing } = useWrappedShare()
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === 'summary')

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  // Must call all hooks before any conditional returns (React rules of hooks)
  const firstName = useAppSelector(state => state.auth.account.givenName)

  // Memoize personality image
  const personalityImage = useMemo(() => {
    if (!data) return undefined
    return getImageUrl(PERSONALITY_IMAGE_IDS[data.personality.type], {
      format: 'png',
      dpr: 3,
      width: 120,
    })
  }, [data])

  const { displayDistance, unitLabel } = useDistanceDisplay(data?.stats?.totalDistanceKm ?? 0)

  if (!data) return null

  const { stats, personality, topDestinations, loyalty, topOffer } = data

  // Low data mode: user has no booking/trip data, only offer viewing data
  const isLowData = stats.citiesVisited.length === 0

  const offerUrl = topOffer?.id ? `/offer/hotel/${topOffer.id}` : undefined
  const visibleInclusions = topOffer?.inclusions?.slice(0, MAX_VISIBLE_INCLUSIONS) ?? []
  const remainingInclusionsCount = (topOffer?.inclusions?.length ?? 0) - MAX_VISIBLE_INCLUSIONS
  const hideSavings = stats.totalSavings < 100

  return (
    <SlideBase
      background={isCurrentSlide ? <WrappedLottieAnimation key="confetti-active" animationName="confetti" position="top" /> : null}
    >
      <Group direction="vertical" gap={24} verticalAlign="end" fullHeight>
        <div>
          <Group direction="vertical" gap={24}>
            <Group direction="vertical" gap={4} fullWidth>
              <Heading variant="heading4" colour="neutral-one">
                <b>{firstName}, Here's Your 2025
                </b>
              </Heading>
              <Heading variant="heading1alt" colour="neutral-one">
                <i>Year of Escapes</i>
              </Heading>
              <BodyText variant="medium" colour="neutral-three">
                What an incredible year!
              </BodyText>
            </Group>
            <Group direction="vertical" gap={12}>
              <TravelVibeCard direction="horizontal" gap={12} verticalAlign="center" fullWidth>
                <PersonalityImageCard className={`personality-${personality.type}`} paletteType="default">
                  <PersonalityImageContainer>
                    <PersonalityImage src={personalityImage} alt={PERSONALITY_NAMES[personality.type]} />
                    <SparklesOverlay>
                      <SparklesAnimation style={LottieStyle} />
                    </SparklesOverlay>
                  </PersonalityImageContainer>
                </PersonalityImageCard>
                <Group direction="vertical" gap={2} grow style={{ minWidth: 0 }}>
                  <Caption variant="medium" colour="neutral-four">Your travel style was</Caption>
                  <Heading variant="heading4" colour="neutral-one">
                    {PERSONALITY_NAMES[personality.type]}
                  </Heading>
                </Group>
              </TravelVibeCard>

              {isLowData && (
                <StatCard>
                  <Caption variant="small" format="uppercase" colour="neutral-four">
                    Viewed offers
                  </Caption>
                  <BodyText variant="large" weight="bold" colour="neutral-one">
                    {stats.offersViewed} offers
                  </BodyText>
                </StatCard>
              )}

              {isLowData && topOffer?.imageId?.length && (
                <TopViewedOfferCard>
                  <Caption variant="medium" colour="neutral-four">
                    {isTotalsOnlyMode ? 'Most Loved Offer' : 'Top Viewed Offer'}
                  </Caption>
                  <BodyText variant="large" weight="bold" colour="neutral-one">
                    {topOffer.propertyName}
                  </BodyText>
                  {visibleInclusions.length > 0 && (
                    <InclusionsContainer direction="vertical" gap={4}>
                      {visibleInclusions.map((inclusion) => (
                        <OfferInclusionItem
                          key={inclusion.id}
                          inclusion={inclusion}
                          size="small"
                          lineClamp={1}
                        />
                      ))}
                      {remainingInclusionsCount > 0 && (
                        <BodyText variant="small" weight="bold" colour="neutral-one">
                          and more
                        </BodyText>
                      )}
                    </InclusionsContainer>
                  )}
                  {offerUrl && (
                    <InteractiveZone>
                      <TextLink to={offerUrl} target="_blank" size="caption" colour="neutral-one">
                        View this offer
                      </TextLink>
                    </InteractiveZone>
                  )}
                </TopViewedOfferCard>
              )}

              {!isLowData && (
                <StatsGrid>
                  <StatCard>
                    <Caption variant="small" format="uppercase" colour="neutral-four">
                      Destinations explored
                    </Caption>
                    <BodyText variant="large" weight="bold" colour="neutral-one">
                      {stats.citiesVisited.length === 1
                        ? '1 city'
                        : `${stats.citiesVisited.length} cities`}{' '}
                      in{' '}
                      {stats.countriesVisited === 1
                        ? '1 country'
                        : `${stats.countriesVisited} countries`}
                    </BodyText>
                  </StatCard>

                  <StatCard>
                    <Caption variant="small" format="uppercase" colour="neutral-four">
                      Nights away
                    </Caption>
                    <BodyText variant="large" weight="bold" colour="neutral-one">
                      {stats.totalNights} nights
                    </BodyText>
                  </StatCard>

                  <StatCard className={clsx({
                    'full-width': hideSavings,
                  })}
                  >
                    <Caption variant="small" format="uppercase" colour="neutral-four">
                      Distance travelled
                    </Caption>
                    <BodyText variant="large" weight="bold" colour="neutral-one">
                      <FormatDecimal value={displayDistance} maximumFractionDigits={0} /> {unitLabel}
                    </BodyText>
                  </StatCard>

                  {!hideSavings && <StatCard>
                    <Caption variant="small" format="uppercase" colour="neutral-four">
                      Savings unlocked
                    </Caption>
                    <BodyText variant="large" weight="bold" colour="neutral-one">
                      <FormatCurrency value={stats.totalSavings} currency={stats.currency}></FormatCurrency>
                    </BodyText>
                  </StatCard>}

                  <StatCard>
                    <Caption variant="small" format="uppercase" colour="neutral-four">
                      Top destination
                    </Caption>
                    <BodyText variant="large" weight="bold" colour="neutral-one">
                      {topDestinations[0]?.name ?? '-'}
                    </BodyText>
                  </StatCard>

                  <StatCard>
                    <Caption variant="small" format="uppercase" colour="neutral-four">
                      Viewed offers
                    </Caption>
                    <BodyText variant="large" weight="bold" colour="neutral-one">
                      {stats.offersViewed} offers
                    </BodyText>
                  </StatCard>

                  {loyalty && (
                    <StatCard className="full-width">
                      <Caption variant="small" format="uppercase" colour="neutral-four">
                        Loyalty rewards
                      </Caption>
                      <BodyText variant="large" weight="bold" colour="neutral-one">
                        {loyalty.points.toLocaleString()} points & {loyalty.statusCredits} status credits
                      </BodyText>
                      <LoyaltyRowSlot direction="horizontal" gap={4} verticalAlign="center">
                        <WrappedLoyaltyPercentileBadge percentile={loyalty.percentile} tier={loyalty.tier} />
                      </LoyaltyRowSlot>
                    </StatCard>
                  )}
                </StatsGrid>
              )}
            </Group>
          </Group>
        </div>
        <InteractiveZone>
          <TextButton
            kind="primary"
            shape="round"
            onClick={handleShare}
            startIcon={<SolidShareIcon />}
            fit="full-width"
            disabled={isCapturing}
          >
            {isCapturing ? 'Preparing...' : 'Share your Year of Escapes'}
          </TextButton>
        </InteractiveZone>
      </Group>
    </SlideBase>
  )
}

export default React.memo(SummarySlide)
